(ns manage.adset-decision-tree-page.view.predicate-editor.input-components
  (:require ["@mui/material/Autocomplete$default" :as Autocomplete]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/InputAdornment$default" :as InputAdornment]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/TextField$default" :as TextField]
            ["react-hook-form" :as rhf]
            [cljs-bean.core :refer [->clj]]
            [clojure.string :as str]
            [common.content-function.schemas :as schema-util]
            [common.form :as form]
            [common.util :as util]
            [manage.adset-decision-tree-page.view.predicate-editor.converter
             :refer [trigger->selector get-predicate-details
                     trigger->predicate-expression-details get-property-operators]]
            [manage.adset-decision-tree-page.view.predicate-editor.timezone-util :as tz-util]
            [uix.core :as uix :refer [$ defui]]))

(defui predicate-expression-value-autocomplete
  [{:keys [field-path control label values multiple disabled autocomplete-props
           allow-any-trigger]}]
  ($ form/autocomplete-field
     {:name field-path
      :control control
      :label label
      :field-props
      (fn [{{:keys [value]} :field}]
        (let [options (or (:options autocomplete-props) values [])]
          (merge {:size "small"
                  :multi? (boolean multiple)
                  :filterSelectedOptions (boolean multiple)
                  :fast-options-conversion true
                  :fullWidth true
                  :disabled? (boolean disabled)
                  :options (cond-> options
                             (and allow-any-trigger value
                                  (not-any? #(= (:value %) value) options))
                             (conj {:value value :label value}))}
                 (dissoc autocomplete-props :options))))}))

(defui predicate-expression-value-free-solo-autocomplete
  [{:keys [field-path control label disabled]}]
  (let [[input-value set-input-value!] (uix/use-state "")]
    ($ rhf/Controller
       {:name field-path
        :control control
        :render
        (fn [^js props]
          (let [{{:keys [onChange value]} :field
                 {:keys [error]} :fieldState} (->clj props)
                sanitized-on-change #(->> (map str/trim %)
                                          (remove str/blank?)
                                          distinct
                                          to-array
                                          onChange)
                add-raw-string #(->> (str/split % #"[\n\t,;]+")
                                     (concat (or value [])) sanitized-on-change)
                on-paste (fn [e]
                           (.preventDefault e)
                           (add-raw-string (.. e -clipboardData (getData "text")))
                           (set-input-value! ""))
                on-input-change (fn [_e new-input]
                                  (if (re-find #"[,;]" (or new-input ""))
                                    (do (add-raw-string (or new-input ""))
                                        (set-input-value! ""))
                                    (set-input-value! (or new-input ""))))]
            ($ Autocomplete
               (util/spread-props
                (.-field props)
                {:value (clj->js (or value []))
                 :multiple true
                 :size "small"
                 :fullWidth true
                 :freeSolo true
                 :disabled (boolean disabled)
                 :options #js []
                 :inputValue input-value
                 :onInputChange on-input-change
                 :onChange (fn [_e new-value] (sanitized-on-change new-value))
                 :renderInput
                 (fn [^js params]
                   ($ TextField
                      (util/spread-props #js {:label label
                                              :placeholder (when (empty? value)
                                                             "Add values separated by \",\" \";\" tab or newline")
                                              :slotProps #js {:inputLabel #js {:shrink true}}
                                              :onPaste on-paste
                                              :error (boolean error)}
                                         params)))}))))})))

(defui predicate-expression-value-integer
  [{:keys [field-path control label disabled unit]}]
  ($ form/form-text-field
     {:name field-path
      :control control
      :on-change-fn #(str/replace % #"[^\d-]" "")
      :field-props {:size "small"
                    :disabled (boolean disabled)
                    :InputProps #js {:endAdornment (when (some? unit)
                                                     ($ InputAdornment {:position "end"}
                                                        ($ Chip {:label unit})))}
                    :label label
                    :fullWidth true}}))

(defui predicate-expression-value-string
  [{:keys [field-path control label disabled]}]
  ($ form/form-text-field
     {:name field-path
      :control control
      :field-props {:size "small"
                    :disabled (boolean disabled)
                    :label label
                    :fullWidth true}}))

(defui predicate-expression-value-datetime
  [{:keys [field-path control label disabled parent-node]}]
  (let [timezone (tz-util/get-display-timezone parent-node)
        label-with-tz (tz-util/format-timezone-label label timezone)]
    ($ form/form-date-time-picker
       {:name field-path
        :control control
        :text-field-props {:size "small"}
        :field-props {:disabled (boolean disabled)
                      :label label-with-tz
                      :timezone timezone
                      :sx #js {:width "100%"}}})))

(defui predicate-expression-value-time
  [{:keys [field-path control label disabled]}]
  ($ form/form-time-picker
     {:name field-path
      :control control
      :text-field-props {:size "small"}
      :field-props {:disabled (boolean disabled)
                    :label label
                    :sx #js {:width "100%"}}}))

;; See similar in manage.content-configuration-page.predicate.expression
(defmulti ^:private predicate-expression-value
  :type
  :default "integer")

(defmethod predicate-expression-value "enum"
  [props]
  ($ predicate-expression-value-autocomplete props))

(defmethod predicate-expression-value ["set" "option"]
  [props]
  ($ predicate-expression-value-autocomplete
     (assoc props :multiple true)))

(defmethod predicate-expression-value "integer"
  [props]
  ($ predicate-expression-value-integer props))

(defmethod predicate-expression-value "timestamp"
  [props]
  ($ predicate-expression-value-datetime props))

(defmethod predicate-expression-value "time"
  [props]
  ($ predicate-expression-value-time props))

(defmethod predicate-expression-value "string"
  [{:keys [multiple] :as props}]
  (if multiple
    ($ predicate-expression-value-free-solo-autocomplete props)
    ($ predicate-expression-value-string props)))

(defmethod predicate-expression-value "option"
  [props]
  ($ predicate-expression-value-autocomplete props))

;; "bool" is always true, no need to render anything
;; the operator decides whether it "is"/"is not" supported
(defmethod predicate-expression-value "bool" [_props])

(defmulti predicate-expression :value-type-modifier)

(defmethod predicate-expression :list
  [{:keys [get-field-path] :as props}]
  (predicate-expression-value (assoc props
                                     :multiple true
                                     :field-path (get-field-path :value))))

(defmethod predicate-expression :range
  [{:keys [get-field-path] :as props}]
  ($ Stack {:direction "row" :spacing 1.5}
     (predicate-expression-value (assoc props
                                        :label "From"
                                        :field-path (get-field-path :start)))
     (predicate-expression-value (assoc props
                                        :label "To"
                                        :field-path (get-field-path :end)))))

(defmethod predicate-expression :nil
  [_props])

(defmethod predicate-expression :default
  [{:keys [get-field-path] :as props}]
  (predicate-expression-value (assoc props :field-path (get-field-path :value))))

(defui operands-input [{{:keys [control]} :form-controls
                        :keys [operator-field-path trigger schema get-field-path
                               disabled allow-any-trigger parent-node]}]
  (let [operator (rhf/useWatch #js {:name operator-field-path :control control})
        selector (trigger->selector trigger)]
    (predicate-expression
     (-> (get-predicate-details schema {:trigger trigger :operator operator})
         (merge (get trigger->predicate-expression-details trigger))
         (assoc :label "Value"
                :control control
                :get-field-path get-field-path
                :operator-field-path operator-field-path
                :values (schema-util/find-values selector schema nil)
                :type (schema-util/find-type selector schema)
                :disabled (or disabled (nil? operator) (nil? trigger))
                :allow-any-trigger allow-any-trigger
                :parent-node parent-node)))))

(defui trigger-select [{:keys [schema-output] :as props}]
  (let [options (->> schema-output
                     (remove (fn [[_ v]] (:limited-to-assignments v)))
                     (map (fn [[k v]]
                            {:value (name k)
                             :label (:label v)})))]
    ($ predicate-expression-value-autocomplete
       (assoc props
              :label "Trigger"
              :autocomplete-props {:options options}))))

(defn get-operator-options [{property-type :type :as property}]
  (keep (fn [{:keys [label] operator-key :key}]
          ;; disable "is available" for bool type
          (when (or (not= "bool" property-type) (not= operator-key :available))
            {:value (name operator-key)
             :label (cond-> label
                      ;; for "bool"s the post-fix "supported" is added to make it clear
                      ;; to the user that the respective trigger "is" or "is not" supported
                      (= "bool" property-type) (str " supported"))}))
        (get-property-operators property)))

(defui operator-select [{:keys [selector schema disabled] :as props}]
  ($ predicate-expression-value-autocomplete
     (assoc props
            :label "Operator"
            :disabled (or disabled (nil? selector))
            :autocomplete-props
            {:options (get-operator-options (schema-util/find-property selector schema))})))
